<template>
  <v-row no-gutters>
    <v-col cols="12">
      <draggable-list
        v-bind="$_filterObjectKeys({ ...$props, ...$attrs }, { notAllowedKeys: 'customMenus' })"
        :value.sync="items"
        allow-elements-on-main-level
        :child-items-key="childItemsKey"
        item-title-key="verboseName"
        add-group-path="admin.customMenus.addElement"
        :groups-dots-items="dotsItems"
        :elements-dots-items="dotsItems"
        :disabled="saving"
        @delete="dialogAction('delete', $event)"
        @edit="dialogAction('edit', $event)"
        @addGroup="dialogAction('addGroup', $event)"
      />
    </v-col>
    <main-dialog
      v-if="isMainDialogLoaded"
      :is-visible.sync="isMainDialogVisible"
      v-bind="mainDialogBind"
      @agree="dialogActionHandler"
    />
    <main-input-dialog
      v-if="isMainInputDialogLoaded"
      :is-visible.sync="isMainInputDialogVisible"
      v-bind="mainInputDialogBind"
      @agree="dialogActionHandler"
    >
      <template
        v-for="(input, idx) of mainInputDialogInputs.filter(element => element.dataType)"
        #[`${input.name}.input`]="data"
      >
        <v-col cols="5" :key="idx">
          <data-input v-bind="data" v-model="mainInputDialogPayload[input.name]" />
        </v-col>
      </template>
      <template #elementType.input="data">
        <v-radio-group class="mt-0 pt-0 pb-7 input--type" v-bind="data" v-model="mainInputDialogPayload['elementType']">
          <v-col cols="12" class="pa-0">
            <v-row no-gutters class="pb-2">
              <v-radio :label="$i18n.t('admin.customMenus.actions.elementsTypes.url')" value="tool"></v-radio>
            </v-row>
            <v-row no-gutters>
              <v-radio :label="$i18n.t('admin.customMenus.actions.elementsTypes.array')" value="menu"></v-radio>
            </v-row>
          </v-col>
        </v-radio-group>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DraggableList from '@/components/DraggableList';

export default {
  name: 'CustomMenusDraggableList',
  components: {
    DataInput,
    DraggableList,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    customMenus: {
      type: Array,
      default: () => [],
    },
    disableRelativePath: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    capitalizeCurrentAction() {
      return this.$_capitalize(this.currentAction);
    },
    currentDialogActionData() {
      return this.dialogsActionsData[this.currentAction] || {};
    },
    mainDialogBind() {
      return {
        loading: this.isMainDialogLoading,
        ...(this.isMainDialogVisible
          ? {
              title: this.$i18n.t(`admin.customMenus.actions.${this.currentAction}ElementTitle`),
              body: this.$i18n.t(`admin.customMenus.actions.confirmElement${this.capitalizeCurrentAction}`, {
                name: this.currentActionData.verboseName,
              }),
              agreeText: this.currentDialogActionData.agreePath,
            }
          : {}),
      };
    },
    mainInputDialogBind() {
      return {
        loading: this.isMainInputDialogLoading,
        dialogWidth: '40%',
        ...(this.isMainInputDialogVisible
          ? {
              titleTranslation: this.$i18n.t(`admin.customMenus.actions.${this.currentAction}ElementTitle`),
              agreeText: this.currentDialogActionData.agreePath,
              inputs: this.mainInputDialogInputs,
            }
          : {}),
      };
    },
    mainInputDialogInputs() {
      const elementEditInputs = [
        {
          name: 'verboseName',
          translationPath: 'admin.customMenus.actions.elementVerboseName',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'elementType',
          translationPath: 'admin.customMenus.actions.elementType',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dense: true,
          row: true,
          hideDetails: true,
        },
        ...(this.mainInputDialogPayload['elementType'] === 'tool'
          ? [
              {
                name: 'routePath',
                translationPath: 'admin.customMenus.actions.url',
                cols: '12',
                ...(this.disableRelativePath
                  ? { rules: [v => !!v || 'rules.required', v => this.$_isValidUrl(v) || 'rules.notValidUrl'] }
                  : { rules: [v => !!v || v == '0' || 'rules.required'] }),
                dataType: {
                  name: 'text',
                },
                prependInnerIcon: 'mdi-web',
              },
            ]
          : []),
      ];
      const mainInputDialogInputsDict = {
        addGroup: elementEditInputs,
        edit: elementEditInputs,
      };
      return (mainInputDialogInputsDict[this.currentAction] || []).map(input => {
        return { ...input, value: JSON.parse(JSON.stringify(this.currentActionData?.[input.name] || '')) };
      });
    },
  },
  data: () => ({
    childItemsKey: 'tools',
    currentAction: '',
    currentActionData: undefined,
    dialogsActionsData: {
      addGroup: {
        agreePath: 'dialog.agreeAdd',
        component: 'MainInputDialog',
        initAction: 'setMainInputDialogPayload',
      },
      delete: {
        agreePath: 'dialog.agreeDelete',
        component: 'MainDialog',
      },
      edit: {
        agreePath: 'dialog.agreeSave',
        component: 'MainInputDialog',
        initAction: 'setMainInputDialogPayload',
      },
    },
    dotsItems: [{ name: 'edit' }, { name: 'delete' }],
    items: [],
    itemsChanged: false,
    isMainDialogLoaded: false,
    isMainDialogLoading: false,
    isMainDialogVisible: false,
    isMainInputDialogLoaded: false,
    isMainInputDialogLoading: false,
    isMainInputDialogVisible: false,
    mainInputDialogPayload: {},
    saving: false,
  }),
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    dialogAction(type, value) {
      this.currentAction = type;
      this.currentActionData = value;
      const { component, initAction } = this.dialogsActionsData[type];
      if (initAction) this[initAction](type, value);
      this[`is${component}Loaded`] = true;
      this[`is${component}Loading`] = false;
      this[`is${component}Visible`] = true;
    },
    async dialogActionHandler() {
      const dialogComponent = this.currentDialogActionData?.component;
      this[`is${dialogComponent}Loading`] = true;
      try {
        await this[`handler${this.capitalizeCurrentAction}`]();
        this[`is${dialogComponent}Loading`] = false;
      } catch {
        this[`is${dialogComponent}Loading`] = false;
      }
    },
    handlerAddGroup() {
      const { elementType, verboseName, routePath } = this.mainInputDialogPayload;
      const newElement = {
        verboseName,
        ...(elementType === 'tool' ? { routePath } : {}),
        ...(elementType === 'menu' ? { [this.childItemsKey]: [] } : {}),
      };
      this.items = [...this.items, ...[newElement]];
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    handlerDelete() {
      const keyToDeleteValues = (this.currentActionData?.elementKey || '')
        .split('-')
        .map(item => parseInt(item))
        .splice(1);
      const keyToDeleteValuesLast = keyToDeleteValues.pop();
      let currentList = this.items;
      keyToDeleteValues.forEach(keyValue => {
        currentList = currentList[keyValue]?.[this.childItemsKey];
      });
      const elementToDelete = JSON.parse(JSON.stringify(currentList[keyToDeleteValuesLast]));
      this.$delete(currentList, keyToDeleteValuesLast);
      if (elementToDelete[this.childItemsKey]) {
        this.items = [...this.items, ...elementToDelete[this.childItemsKey]];
      }
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    handlerEdit() {
      const { elementType, verboseName, routePath } = this.mainInputDialogPayload;
      const keyToEditValues = (this.currentActionData?.elementKey || '')
        .split('-')
        .map(item => parseInt(item))
        .splice(1);
      const keyToEditValuesLast = keyToEditValues.pop();
      let currentList = this.items;
      keyToEditValues.forEach(keyValue => {
        currentList = currentList[keyValue]?.[this.childItemsKey];
      });
      let childrenToSave = [];
      const elementToEdit = JSON.parse(JSON.stringify(currentList[keyToEditValuesLast]));
      if (elementToEdit[this.childItemsKey] && elementType === 'tool') {
        childrenToSave = JSON.parse(JSON.stringify(elementToEdit[this.childItemsKey]));
        delete elementToEdit[this.childItemsKey];
      } else if (elementToEdit.routePath && elementType === 'menu') {
        delete elementToEdit.routePath;
        elementToEdit[this.childItemsKey] = [];
      }
      this.$set(currentList, keyToEditValuesLast, {
        ...elementToEdit,
        verboseName,
        ...(elementType === 'tool' ? { routePath } : {}),
      });
      if (childrenToSave?.length) {
        this.items = [...this.items, ...childrenToSave];
      }
      this[`is${this.currentDialogActionData?.component}Visible`] = false;
    },
    setMainInputDialogPayload(type, value) {
      if (type === 'addGroup') {
        this.mainInputDialogPayload = { verboseName: '', elementType: 'tool', routePath: '' };
      } else if (type === 'edit') {
        this.mainInputDialogPayload = {
          verboseName: value.verboseName,
          elementType: value[this.childItemsKey] ? 'menu' : 'tool',
          routePath: value.routePath || '',
        };
      }
    },
    async save() {
      if (!this.itemsChanged) return;
      this.saving = true;
      try {
        await this.editSingleSetting({
          key: 'custom_app_menu',
          body: {
            value: JSON.parse(JSON.stringify(this.items)),
          },
        });
        this.itemsChanged = false;
        this.saving = false;
      } catch {
        this.saving = false;
      }
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.itemsChanged = true;
      },
    },
  },
  mounted() {
    this.$watch(
      vm => [vm.isMainDialogVisible, vm.isMainInputDialogVisible],
      nV => {
        const [isMainDialogVisible, isMainInputDialogVisible] = nV;
        if (!isMainDialogVisible && !isMainInputDialogVisible) {
          this.currentAction = '';
          this.isMainDialogLoading = false;
          this.isMainInputDialogLoading = false;
        }
      }
    );
    this.items = JSON.parse(JSON.stringify(this.customMenus));
    this.$nextTick(() => {
      this.itemsChanged = false;
    });
  },
};
</script>
<style lang="scss" scoped>
.input--type {
  ::v-deep {
    .v-icon {
      font-size: 22px;
    }
    .v-label {
      font-size: 16px;
      padding-left: 5px;
      color: map-get($colors, 'font');
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
}
</style>
